import { supabase } from '@/core/database/supabase';
import type { EstimateCreateInput, EstimateHeader, EstimateListFilters, EstimateListStats } from '../types/estimate.types';

function mapBoqToEstimate(row: any): EstimateHeader {
  return {
    id: row.id,
    company_id: row.company_id,
    project_id: row.project_id,
    estimate_code: row.boq_code,
    estimate_name: row.boq_name,
    estimate_type: 'project',
    status_code: row.status_code === 'locked' ? 'converted_to_boq' : row.status_code,
    version_no: row.version_no || 1,
    description: row.description,
    customer_name: row.project?.name || null,
    total_amount: Number(row.total_amount || 0),
    created_at: row.created_at,
    updated_at: row.updated_at,
    project: row.project || null,
    source: 'construction_boqs',
  };
}

function calcStats(items: EstimateHeader[]): EstimateListStats {
  return {
    total: items.length,
    draft: items.filter((item) => item.status_code === 'draft').length,
    reviewing: items.filter((item) => item.status_code === 'reviewing').length,
    approved: items.filter((item) => item.status_code === 'approved').length,
    locked: items.filter((item) => item.status_code === 'locked').length,
    converted: items.filter((item) => item.status_code === 'converted_to_boq').length,
    totalAmount: items.reduce((sum, item) => sum + Number(item.total_amount || 0), 0),
  };
}

export const estimateHeadersService = {
  async create(input: EstimateCreateInput): Promise<EstimateHeader> {
    const { data, error } = await supabase
      .from('estimate_headers' as any)
      .insert({
        company_id: input.company_id,
        project_id: input.project_id || null,
        estimate_code: input.estimate_code,
        estimate_name: input.estimate_name,
        estimate_type: input.estimate_type || 'project',
        customer_name: input.customer_name || null,
        description: input.description || null,
        status_code: 'draft',
        version_no: 1,
        created_by: input.created_by || null,
      })
      .select(`
        *,
        project:projects(id, name)
      `)
      .single();

    if (error) throw error;
    return {
      ...(data as any),
      total_amount: Number((data as any).total_amount || 0),
      source: 'estimate_headers',
    } as EstimateHeader;
  },

  async getList(companyId: string, filters: EstimateListFilters = {}): Promise<{ items: EstimateHeader[]; stats: EstimateListStats }> {
    const estimateRows = await this.fetchEstimateHeaders(companyId, filters);
    const items = estimateRows.length > 0 ? estimateRows : await this.fetchBoqFallback(companyId, filters);
    return { items, stats: calcStats(items) };
  },

  async getById(id: string): Promise<EstimateHeader | null> {
    const estimateRow = await this.fetchEstimateHeaderById(id);
    if (estimateRow) return estimateRow;
    return this.fetchBoqFallbackById(id);
  },

  async updateStatus(id: string, statusCode: string): Promise<void> {
    const { error } = await supabase
      .from('estimate_headers' as any)
      .update({ status_code: statusCode, updated_at: new Date().toISOString() })
      .eq('id', id);

    if (error) throw error;
  },

  async fetchEstimateHeaderById(id: string): Promise<EstimateHeader | null> {
    const { data, error } = await supabase
      .from('estimate_headers' as any)
      .select(`
        *,
        project:projects(id, name)
      `)
      .eq('id', id)
      .maybeSingle();

    if (error) {
      const missingTable = error.code === '42P01' || error.message?.includes('estimate_headers');
      if (missingTable) return null;
      throw error;
    }

    if (!data) return null;
    return {
      ...(data as any),
      total_amount: Number((data as any).total_amount || 0),
      source: 'estimate_headers',
    } as EstimateHeader;
  },

  async fetchEstimateHeaders(companyId: string, filters: EstimateListFilters): Promise<EstimateHeader[]> {
    let query = supabase
      .from('estimate_headers' as any)
      .select(`
        *,
        project:projects(id, name)
      `)
      .eq('company_id', companyId);

    if (filters.status && filters.status !== 'all') {
      query = query.eq('status_code', filters.status);
    }

    if (filters.search) {
      query = query.or(`estimate_code.ilike.%${filters.search}%,estimate_name.ilike.%${filters.search}%,customer_name.ilike.%${filters.search}%`);
    }

    if (filters.project_id) {
      query = query.eq('project_id', filters.project_id);
    }

    if (filters.created_by) {
      query = query.eq('created_by', filters.created_by);
    }

    if (filters.date_from) {
      query = query.gte('created_at', filters.date_from);
    }

    if (filters.date_to) {
      query = query.lte('created_at', filters.date_to);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) {
      const missingTable = error.code === '42P01' || error.message?.includes('estimate_headers');
      if (missingTable) return [];
      throw error;
    }

    return ((data || []) as any[]).map((row) => ({
      ...row,
      total_amount: Number(row.total_amount || 0),
      source: 'estimate_headers' as const,
    }));
  },

  async fetchBoqFallback(companyId: string, filters: EstimateListFilters): Promise<EstimateHeader[]> {
    let query = supabase
      .from('construction_boqs')
      .select(`
        *,
        project:projects(id, name)
      `)
      .eq('company_id', companyId);

    if (filters.status && filters.status !== 'all') {
      query = query.eq('status_code', filters.status === 'converted_to_boq' ? 'locked' : filters.status);
    }

    if (filters.search) {
      query = query.or(`boq_code.ilike.%${filters.search}%,boq_name.ilike.%${filters.search}%`);
    }

    if (filters.project_id) {
      query = query.eq('project_id', filters.project_id);
    }

    if (filters.created_by) {
      query = query.eq('created_by', filters.created_by);
    }

    if (filters.date_from) {
      query = query.gte('created_at', filters.date_from);
    }

    if (filters.date_to) {
      query = query.lte('created_at', filters.date_to);
    }

    const { data, error } = await query.order('created_at', { ascending: false });
    if (error) throw error;

    return ((data || []) as any[]).map(mapBoqToEstimate);
  },

  async fetchBoqFallbackById(id: string): Promise<EstimateHeader | null> {
    const { data, error } = await supabase
      .from('construction_boqs')
      .select(`
        *,
        project:projects(id, name)
      `)
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;
    return mapBoqToEstimate(data);
  },
};
